import type { HostnameCheck } from '@cdwr/fly-node/api';

/** Short technical words worth keeping upper-case in a humanized code */
const ACRONYMS = new Set([
  'dns',
  'acme',
  'ipv4',
  'ipv6',
  'txt',
  'cname',
  'ttl',
  'caa',
  'soa'
]);

/**
 * Turn a Fly error code into something readable, since none of them come
 * with a translation. `IPV6_NOT_FOUND` becomes `IPV6 not found` — not as
 * polished as a hand-written sentence, but nothing a reader has to decode.
 */
const humanize = (code: string): string =>
  code
    .toLowerCase()
    .split('_')
    .map((word) => (ACRONYMS.has(word) ? word.toUpperCase() : word))
    .join(' ')
    .replace(/^./, (char) => char.toUpperCase());

/**
 * What to tell a domain's owner is wrong, in words rather than codes.
 *
 * Fly describes a problem two different ways depending on when it is asked.
 * `AppCertificate.validationErrors` is prose, written once Let's Encrypt has
 * actually tried and failed — the same wording Fly's own dashboard shows.
 * `HostnameCheck.errors` is available earlier, before any attempt has been
 * made, but only as SCREAMING_SNAKE_CASE codes with no prose counterpart.
 *
 * The prose wins whenever there is any, since it is Fly's own words rather
 * than a guess at what a code means — but only *before* the certificate is
 * issued. `validationErrors` is a log of failed attempts, oldest first, and
 * Payload's stored row keeps no timestamps to age it out — a domain that was
 * briefly misconfigured before it validated would otherwise show that prose
 * forever. Once `isConfigured` is true, only a live check has standing to
 * speak about the domain's current state.
 *
 * A live check can also name a problem it isn't actually blocked on: Fly
 * reports an ownership-verification code without always offering the TXT
 * record (`dnsVerificationRecord`) that would satisfy it, even on a domain
 * that is issued and serving — Fly's own dashboard does not show it either
 * in that state. An ownership code with no record to act on is dropped
 * rather than shown as if there were something to do about it.
 *
 * @param certificate - Stored state, which is where validation prose lives.
 * Loosely typed rather than `Pick<CertificateState, ...>` because Payload's
 * generated row type makes every stored field optional, not just nullable
 * @param check - A live dns check, only present right after one was run
 */
export const describeCertificateIssues = (
  certificate:
    | {
        isConfigured?: boolean | null;
        validationErrors?: Array<string> | null;
      }
    | null
    | undefined,
  check:
    | Pick<HostnameCheck, 'errors' | 'dnsVerificationRecord'>
    | null
    | undefined
): Array<string> => {
  if (!certificate?.isConfigured && certificate?.validationErrors?.length) {
    return certificate.validationErrors;
  }

  const hasOwnershipRecord = Boolean(check?.dnsVerificationRecord);

  return (check?.errors ?? [])
    .filter((code) => hasOwnershipRecord || !/ownership/i.test(code))
    .map(humanize);
};
